/**
 * Glyphes de la barre d'onglets — SVG en ligne, tracés à la main.
 *
 * **Pourquoi pas une bibliothèque d'icônes.** Quatre glyphes ne justifient ni
 * une dépendance de plus, ni les 20 à 60 ko qu'elle pèserait, ni un chargement
 * réseau sur le chemin critique. Écrits ici, ils héritent de `currentColor` et
 * suivent donc l'état actif de l'onglet sans code supplémentaire.
 *
 * **Dessin.** Esprit SF Symbols : grille 24, contour seul (`fill: none`),
 * épaisseur 1,7, extrémités et jonctions arrondies. Le contour plutôt que le
 * plein parce que l'onglet actif se distingue déjà par la teinte et par sa
 * pastille — remplir en plus ferait deux signaux pour une seule information.
 *
 * ⚠️ Les glyphes sont `aria-hidden` : **le libellé texte de l'onglet porte
 * seul l'information.** Une icône n'est jamais le seul indice de ce que fait un
 * onglet, et « Bibliothèque » ne se devine pas d'une pile de livres.
 */

import type { ReactNode } from 'react';

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg
      className="ui-glyph"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Aujourd'hui — couvert (`fork.knife`). Le journal du jour, c'est ce qu'on mange. */
export function IconToday() {
  return (
    <Glyph>
      {/* Fourchette : trois dents, tête en U, manche. */}
      <path d="M5.8 3.5v5.2a2.4 2.4 0 0 0 4.8 0V3.5" />
      <path d="M8.2 3.5v4.8" />
      <path d="M8.2 11.1v9.4" />
      {/*
        Couteau : dos rectiligne, lame renflée qui rejoint le manche.
        ⚠️ La lame a été élargie et rapprochée de la fourchette après examen du
        rendu à 24 px : à sa largeur d'origine elle s'effondrait en un simple
        trait vertical et l'icône se lisait « deux fourchettes ».
      */}
      <path d="M14 3.5v17" />
      <path d="M14 3.5c2.5 2.2 3.9 5.3 3.6 8.3a1.6 1.6 0 0 1-1.6 1.4h-2" />
    </Glyph>
  );
}

/** Bibliothèque — livres rangés (`books.vertical`), dont un appuyé sur les autres. */
export function IconLibrary() {
  return (
    <Glyph>
      {/*
        ⚠️ Largeur 4,5 et écart 2,8 : ce n'est pas approximatif. Le trait fait
        1,7 et déborde donc de 0,85 de chaque côté du tracé — sous 2,6 d'écart,
        les livres se **touchent** à 24 px et l'icône devient un pavé plein.
      */}
      <rect x="2.4" y="3.6" width="4.5" height="16.8" rx="1.3" />
      <rect x="9.7" y="3.6" width="4.5" height="16.8" rx="1.3" />
      {/* Pivot sur le bas du livre, pour qu'il s'incline sans décoller du sol. */}
      <rect x="17" y="4.8" width="4.5" height="15.6" rx="1.3" transform="rotate(-8 19.25 20.4)" />
    </Glyph>
  );
}

/**
 * Muscu — haltère (`dumbbell`), le 5ᵉ onglet de la V2 (§2.5).
 *
 * ⚠️ **Une barre et quatre disques, pas une silhouette.** Un bonhomme qui
 * soulève quelque chose se lit mal à 24 px en contour seul, et il désigne la
 * personne plutôt que l'objet ; l'haltère est ce que SF Symbols emploie et ce
 * que l'œil reconnaît de loin.
 *
 * ⚠️ Les disques intérieurs sont plus hauts que les extérieurs, comme sur une
 * barre chargée. À largeur égale, l'icône se lisait « quatre traits » — c'est
 * le même piège que la lame de couteau d'`IconToday`, mesuré au même endroit.
 */
export function IconSessions() {
  return (
    <Glyph>
      {/* Barre. */}
      <path d="M7.4 12h9.2" />
      {/* Disques intérieurs, les plus hauts. */}
      <path d="M7.4 8.1v7.8" />
      <path d="M16.6 8.1v7.8" />
      {/* Disques extérieurs. */}
      <path d="M4.6 9.6v4.8" />
      <path d="M19.4 9.6v4.8" />
      {/* Manchons : ce sont eux qui font lire « barre » et non « échelle ». */}
      <path d="M2.4 10.9v2.2" />
      <path d="M21.6 10.9v2.2" />
    </Glyph>
  );
}

/** Progression — courbe croissante sur deux axes (`chart.line.uptrend.xyaxis`). */
export function IconProgress() {
  return (
    <Glyph>
      <path d="M3.5 3v15.5a2 2 0 0 0 2 2H21" />
      <path d="M7 16.2l3.4-4.1 2.9 2.4 4.3-5.2" />
      {/* Pointe de flèche : les deux barbes se rejoignent au bout de la courbe. */}
      <path d="M14.9 9.3h2.7v2.7" />
    </Glyph>
  );
}

/**
 * Réglages — engrenage (`gearshape`).
 *
 * ⚠️ Tracé **calculé, pas dessiné à la main** : 8 dents réparties au pas de 45°,
 * rayon de tête 9,1, rayon de pied 6,7, demi-angle de dent 11°. Le retoucher au
 * jugé désaligne les dents ; régénérer plutôt la boucle qui l'a produit.
 */
export function IconSettings() {
  return (
    <Glyph>
      <path d="M20.93 10.26A9.1 9.1 0 0 1 20.93 13.74L18.58 13.28A6.7 6.7 0 0 1 17.55 15.75L19.54 17.09A9.1 9.1 0 0 1 17.09 19.54L15.75 17.55A6.7 6.7 0 0 1 13.28 18.58L13.74 20.93A9.1 9.1 0 0 1 10.26 20.93L10.72 18.58A6.7 6.7 0 0 1 8.25 17.55L6.91 19.54A9.1 9.1 0 0 1 4.46 17.09L6.45 15.75A6.7 6.7 0 0 1 5.42 13.28L3.07 13.74A9.1 9.1 0 0 1 3.07 10.26L5.42 10.72A6.7 6.7 0 0 1 6.45 8.25L4.46 6.91A9.1 9.1 0 0 1 6.91 4.46L8.25 6.45A6.7 6.7 0 0 1 10.72 5.42L10.26 3.07A9.1 9.1 0 0 1 13.74 3.07L13.28 5.42A6.7 6.7 0 0 1 15.75 6.45L17.09 4.46A9.1 9.1 0 0 1 19.54 6.91L17.55 8.25A6.7 6.7 0 0 1 18.58 10.72L20.93 10.26Z" />
      <circle cx="12" cy="12" r="3.1" />
    </Glyph>
  );
}
